// web/app/src/views/SuperAdmin/Management/LeadManagement/components/LeadForm.tsx
import {
  Alert,
  alpha,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Menu,
  useMediaQuery,
  useTheme,
  Theme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import {
  Lead,
  LeadStatus,
  TruckRequest,
  StatusHistory,
  LeadBusinessContact,
  LeadBusinessContactRole,
} from '@otw/models/leads/lead'
import type { ContactCompany } from '@otw/models/leads/contactCompanies'
import type { PrivateUserInfoType, NotificationPreferences } from '@otw/models'
import { notificationEnums } from '@otw/models'
import { Vehicle } from '@otw/models/inventory/inventory'
import { useAuth } from 'contexts/AuthContext'
import { useLeads } from 'hooks/admin/useLeads'
import { useInventory } from 'hooks/inventory/useInventory'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { useNotificationSubscribers } from 'hooks/useNotificationSubscribers'
import { getUserDisplayName, useSalesAssignableUsers, useUsers } from 'hooks/useUsers'
import React, { useEffect, useMemo, useState } from 'react'
import { Controller, FieldErrors, useForm } from 'react-hook-form'
import { TruckRequestForm } from './index'
import { UserSelector } from '@/components/UserSelector'
import ClearIcon from '@mui/icons-material/Clear'
import MenuIcon from '@mui/icons-material/Menu'
import PersonIcon from '@mui/icons-material/Person'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import LinkIcon from '@mui/icons-material/Link'
import DescriptionIcon from '@mui/icons-material/Description'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import ApartmentIcon from '@mui/icons-material/Apartment'
import ContactsIcon from '@mui/icons-material/Contacts'
import { usePickupLocations } from 'hooks/inventory/usePickupLocations'
import PickupLocationSelector from '../PickupLocationSelector'
import { useContactCompanies } from 'hooks/contacts/useContactCompanies'
import { useContacts } from 'hooks/contacts/useContacts'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { sortCompanyMemberContacts } from '../../util/companyLocationPrefill'
import {
  canMoveLeadToClosed,
  CLOSED_DROPFF_REQUIREMENT_MESSAGE,
} from '../../util/leadDropoffValidation'
import { resolvePickupLocationById } from '../../util/pickupLocationDisplay'
import {
  formatVehicleAddedSizeKeyLabel,
  getLeadVehicleAddedSizeKeys,
  getUncoveredVehicleAddedSizeKeys,
} from '../../util/leadVehicleAddedSizeKeys'
import { getContactPrimaryCompanyName } from '../../util/contactCompanyMembership'
import type {
  CompanyMemberForLead,
  ContactPrefillForLead,
  InitialClientForLead,
} from '../dialogs'
import ClientDriversLicenseSection from '../ClientDriversLicenseSection'
import { usePermissions } from 'hooks/auth/usePermissions'

/** Prefer the first non-empty trimmed string (user → contact / initialClient → form). */
const firstNonEmpty = (...values: Array<string | null | undefined>): string => {
  for (const value of values) {
    const trimmed = value?.trim()
    if (trimmed) return trimmed
  }
  return ''
}

/** Optional field seeds when linking a portal user (e.g. CRM contact / Create User payload). */
type ClientFieldFallbacks = {
  customerName?: string
  email?: string
  phone?: string
  company?: string
}

interface LeadFormProps {
  selectedLead: Lead | null
  onClearSelection: () => void
  isCreating: boolean
  hideClientSearch?: boolean
  hideCancel?: boolean
  initialClient?: InitialClientForLead | null
  contactPrefill?: ContactPrefillForLead | null
  /** When true, create is blocked until UserSelector has a real portal client. */
  requireLinkedClient?: boolean
  /**
   * The {@link ContactCompany} the lead belongs to (only used in `isCreating`
   * mode). When set, the form prefills the company name, company id, business
   * address from this record, and the Business Contact section
   * switches to a picker over {@link companyMembers}.
   */
  selectedCompany?: ContactCompany | null
  /** Hydrated member contacts of {@link selectedCompany}; populated by the caller. */
  companyMembers?: CompanyMemberForLead[]
  /** Pre-select this contact in the business-contact picker and prefill their company role. */
  defaultBusinessContactId?: string | null
  /** CRM contact id written to `Lead.contactId` on create (and kept on edit if already set). */
  initialContactId?: string | null
  /** Called after a new lead is persisted (before the dialog closes via onClearSelection). */
  onLeadCreated?: (lead: Lead) => void | Promise<void>
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`lead-tabpanel-${index}`}
      aria-labelledby={`lead-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            p: isSmallScreen ? 1 : 3,
            px: isSmallScreen ? 0 : 3,
          }}
        >
          {children}
        </Box>
      )}
    </div>
  )
}

interface TabDef {
  label: string
  shortLabel: string
  icon: React.ReactElement
}

const BUSINESS_CONTACT_ROLES: { value: LeadBusinessContactRole; label: string }[] = [
  { value: 'owner', label: 'Owner' },
  { value: 'manager', label: 'Manager' },
  { value: 'partner', label: 'Partner' },
]

const FORM_TABS: TabDef[] = [
  { label: 'Basic Info', shortLabel: 'Basics', icon: <PersonIcon fontSize='small' /> },
  { label: 'Truck Preferences', shortLabel: 'Trucks', icon: <LocalShippingIcon fontSize='small' /> },
  { label: 'Inventory & Applications', shortLabel: 'Inventory', icon: <LinkIcon fontSize='small' /> },
]

/** US phone input mask: (123) 456-7890 */
const formatDigitsAsUsPhone = (raw: string): string => {
  const input = raw.replace(/\D/g, '').slice(0, 10)
  if (input.length <= 3) return input
  if (input.length <= 6) return `(${input.slice(0, 3)}) ${input.slice(3)}`
  return `(${input.slice(0, 3)}) ${input.slice(3, 6)}-${input.slice(6)}`
}

export const LeadForm: React.FC<LeadFormProps> = ({
  selectedLead,
  onClearSelection,
  isCreating,
  hideClientSearch = false,
  hideCancel = false,
  initialClient = null,
  contactPrefill = null,
  requireLinkedClient = false,
  selectedCompany = null,
  companyMembers,
  defaultBusinessContactId = null,
  initialContactId = null,
  onLeadCreated,
}) => {
  const theme = useTheme()
  const { createLead, updateLead, leads } = useLeads()
  const { vehicles } = useInventory('master_inventory')
  const { users } = useUsers()
  const { users: assignableReps, isLoading: isLoadingAssignableReps } = useSalesAssignableUsers()
  const { userInfo } = useAuth()
  const { notificationSubscriptions, updateNotificationPreferences } = useNotificationSubscribers(
    userInfo?.id || '',
  )
  const { checkPermission } = usePermissions()
  const hasLeadEditPermission = checkPermission('lead_management', 'modify')
  const isMobile = useIsMobile()
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const accent = theme.palette.primary.main
  const [tabValue, setTabValue] = useState(0)
  const [selectedUser, setSelectedUser] = useState<PrivateUserInfoType | null>(null)
  const selectedClientForLicense = useMemo(() => {
    if (!selectedUser?.id || !users?.length) return selectedUser
    return users.find(user => user.id === selectedUser.id) ?? selectedUser
  }, [selectedUser, users])
  const appliedInitialClientIdRef = React.useRef<string | null>(null)
  const appliedContactPrefillKeyRef = React.useRef<string | null>(null)
  const appliedSelectedCompanyIdRef = React.useRef<string | null>(null)
  const appliedDefaultBusinessContactIdRef = React.useRef<string | null>(null)
  /** Ensures we only auto-link the portal client once when opening an edit form. */
  const appliedLinkedClientForLeadIdRef = React.useRef<string | null>(null)
  const [clientLinkRequiredError, setClientLinkRequiredError] = useState(false)
  /** After unlinking a client on an existing lead, save is blocked until a new client is selected. */
  const [mustRelinkClient, setMustRelinkClient] = useState(false)
  const [userApplications, setUserApplications] = useState<string[]>([])
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null)

  // Add state for duplicate lead warning dialog
  const [duplicateWarningOpen, setDuplicateWarningOpen] = useState(false)
  const [formData, setFormData] = useState<Lead | null>(null)
  const [duplicateReason, setDuplicateReason] = useState<string[]>([])
  const [duplicateLead, setDuplicateLead] = useState<Lead | null>(null)

  // Vehicle Added notification opt-in after create when lead sizes are not covered
  const [vehicleAddedPromptOpen, setVehicleAddedPromptOpen] = useState(false)
  const [pendingVehicleAddedSizes, setPendingVehicleAddedSizes] = useState<string[]>([])
  const [enablingVehicleAddedSizes, setEnablingVehicleAddedSizes] = useState(false)

  // Add state for other source
  const [otherSource, setOtherSource] = useState<string>('')
  const { pickupLocations: savedPickupLocations } = usePickupLocations()
  const { companies: contactCompanies } = useContactCompanies()
  const { contacts: crmContacts } = useContacts()
  const { showError } = useSnackbar()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    clearErrors,
    setValue,
    getValues,
    watch,
    trigger,
  } = useForm<Lead>({
    defaultValues: selectedLead
      ? {
          ...selectedLead,
          phone: selectedLead.phone ? formatDigitsAsUsPhone(selectedLead.phone) : '',
          client_id: selectedLead.client_id ?? null,
          contactId: selectedLead.contactId ?? null,
          companyId: selectedLead.companyId ?? null,
          businessAddressId: selectedLead.businessAddressId ?? null,
          businessContact: {
            name: selectedLead.businessContact?.name ?? '',
            email: selectedLead.businessContact?.email ?? '',
            phone: selectedLead.businessContact?.phone
              ? formatDigitsAsUsPhone(selectedLead.businessContact.phone)
              : '',
            role: selectedLead.businessContact?.role ?? undefined,
          },
        }
      : {
          status: 'new' as LeadStatus,
          prevStatus: null,
          financingStatus: '',
          timeFrame: '',
          source: '',
          priority: 'medium',
          truckIds: [],
          applicationIds: [],
          followUpNotes: [],
          statusHistory: [],
          tags: [],
          truckRequest: { types: [], notes: '' },
          client_id: null,
          contactId: null,
          companyId: null,
          businessAddressId: null,
          truckDropoffLocationIds: {},
          businessContact: { name: '', email: '', phone: '', role: undefined },
        },
  })

  const companyField = watch('company')

  // Filter for active inventory only
  const activeVehicles =
    vehicles?.filter(
      vehicle =>
        vehicle.status === 'Available' || vehicle.status === 'Pending Available' || vehicle.status === 'Active Deal',
    ) || []

  const financingStatuses = ['No Credit', 'Bad Credit', 'Good Credit', 'Excellent Credit', 'Not Applicable']

  const leadStatuses: LeadStatus[] = ['new', 'inProgress', 'waitingFinance', 'closed', 'lost']

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
    setMobileMenuAnchor(null) // Close menu after selection
  }

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMobileMenuAnchor(event.currentTarget)
  }

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null)
  }

  const handleMobileMenuSelect = (index: number) => {
    setTabValue(index)
    setMobileMenuAnchor(null)
  }

  const handleUserSelect = (
    user: PrivateUserInfoType,
    fieldFallbacks?: ClientFieldFallbacks,
  ) => {
    setClientLinkRequiredError(false)
    setMustRelinkClient(false)
    setSelectedUser(user)
    setValue('client_id', user.id, { shouldDirty: true })

    // Portal users often lack phone (and sometimes other fields) that the linked
    // CRM contact already has — prefer user, then explicit fallbacks, then contact,
    // then any values already on the create form (e.g. contactPrefill).
    const linkedContact = crmContacts?.find(
      (contact) => contact.userId?.trim() === user.id,
    )
    if (!getValues('contactId')?.trim() && linkedContact?.id) {
      setValue('contactId', linkedContact.id, { shouldDirty: true })
    }
    const contactCompanyName = linkedContact
      ? getContactPrimaryCompanyName(linkedContact.id, contactCompanies)
      : undefined
    const formCustomerName = isCreating ? getValues('customerName') : ''
    const formEmail = isCreating ? getValues('email') : ''
    const formPhone = isCreating ? getValues('phone') : ''
    const formCompany = isCreating ? getValues('company') : ''

    const customerName = firstNonEmpty(
      `${user.first_name ?? ''} ${user.last_name ?? ''}`,
      fieldFallbacks?.customerName,
      linkedContact?.name,
      formCustomerName,
    )
    const email = firstNonEmpty(
      user.email,
      fieldFallbacks?.email,
      linkedContact?.email,
      formEmail,
    )
    const phoneRaw = firstNonEmpty(
      user.phone,
      fieldFallbacks?.phone,
      linkedContact?.phone,
      formPhone,
    )
    const company = firstNonEmpty(
      user.company_name,
      fieldFallbacks?.company,
      contactCompanyName,
      formCompany,
    )

    setValue('customerName', customerName, { shouldDirty: true, shouldValidate: true })
    setValue('email', email, { shouldDirty: true, shouldValidate: true })
    setValue('phone', phoneRaw ? formatDigitsAsUsPhone(phoneRaw) : '', {
      shouldDirty: true,
      shouldValidate: true,
    })
    setValue('company', company, { shouldDirty: true, shouldValidate: true })

    // Check if user has applications
    // @ts-ignore - applications property may exist on extended user types
    if (user.applications && Array.isArray(user.applications) && user.applications.length > 0) {
      // @ts-ignore - applications property may exist on extended user types
      const applicationIds = user.applications.map((app: any) => app.id)
      setUserApplications(applicationIds)

      // If creating a new lead, auto-populate application IDs
      if (isCreating) {
        setValue('applicationIds', applicationIds)
      }
    } else {
      setUserApplications([])
    }
  }

  const handleClearUser = () => {
    setSelectedUser(null)
    setUserApplications([])
    setValue('client_id', null, { shouldDirty: true })
    // Don't clear form values if editing an existing lead
    if (isCreating) {
      setValue('customerName', '')
      setValue('email', '')
      setValue('phone', '')
      setValue('company', '')
      setValue('location', '')
      setValue('companyId', null)
      setValue('businessAddressId', null)
      setValue('businessContact', { name: '', email: '', phone: '', role: undefined })
      setValue('applicationIds', [])
      setClientLinkRequiredError(false)
      setMustRelinkClient(false)
    } else {
      // Existing lead: must pick a replacement client before save
      if (selectedLead) {
        appliedLinkedClientForLeadIdRef.current = selectedLead.id
      }
      setMustRelinkClient(true)
      setClientLinkRequiredError(true)
    }
  }

  const onSubmit = async (data: Lead) => {
    const needsLinkedClient =
      (isCreating && requireLinkedClient) || (!isCreating && mustRelinkClient)
    if (needsLinkedClient && !selectedUser) {
      setClientLinkRequiredError(true)
      setTabValue(0)
      return
    }
    setClientLinkRequiredError(false)

    // Create a copy of the data to modify
    const leadData = { ...data }
    leadData.client_id = selectedUser?.id?.trim() || data.client_id?.trim() || null
    leadData.contactId =
      data.contactId?.trim() ||
      selectedLead?.contactId?.trim() ||
      initialContactId?.trim() ||
      null

    const businessLocation = resolvePickupLocationById(
      savedPickupLocations,
      leadData.businessAddressId,
    )
    if (businessLocation?.state?.trim()) {
      leadData.location = businessLocation.state.trim()
    }
    if (!leadData.businessAddressId?.trim()) {
      leadData.businessAddressId = null
    }
    if (!leadData.companyId?.trim()) {
      leadData.companyId = isCreating && selectedCompany?.id ? selectedCompany.id : null
    }
    delete leadData.dropoffLocationId

    if (
      leadData.status === 'closed' &&
      !canMoveLeadToClosed(
        leadData,
        savedPickupLocations,
        contactCompanies,
      )
    ) {
      showError(CLOSED_DROPFF_REQUIREMENT_MESSAGE)
      setTabValue(0)
      return
    }

    // If source is "other", replace it with the actual other source value
    if (leadData.source === 'Other' && otherSource) {
      leadData.source = otherSource
    }

    const bcRaw = leadData.businessContact
    if (bcRaw) {
      const bcName = bcRaw.name?.trim()
      const bcEmail = bcRaw.email?.trim()
      const bcPhone = bcRaw.phone?.trim()
      const bcRole = bcRaw.role
      if (bcName || bcEmail || bcPhone || bcRole) {
        leadData.businessContact = {
          ...(bcName ? { name: bcName } : {}),
          ...(bcEmail ? { email: bcEmail } : {}),
          ...(bcPhone ? { phone: bcPhone } : {}),
          ...(bcRole ? { role: bcRole } : {}),
        }
      } else {
        const hadStoredContact = !!(
          selectedLead?.businessContact?.name?.trim() ||
          selectedLead?.businessContact?.email?.trim() ||
          selectedLead?.businessContact?.phone?.trim() ||
          selectedLead?.businessContact?.role
        )
        if (hadStoredContact) {
          ;(leadData as { businessContact?: LeadBusinessContact | null }).businessContact = null
        } else {
          delete leadData.businessContact
        }
      }
    }

    if (selectedLead) {
      // Check if status has changed
      if (data.status !== selectedLead.status) {
        // Create a new status history entry with date as Date object for type safety
        const currentDate = new Date()
        const statusHistoryEntry: StatusHistory = {
          id: `status_${Date.now()}`,
          previousStatus: selectedLead.status,
          newStatus: data.status as LeadStatus,
          changedBy: userInfo?.id || 'unknown',
          changedAt: currentDate, // Use Date object to match StatusHistory type
          notes: `Status updated from ${selectedLead.status || 'unknown'} to ${data.status}`,
        }

        // Add the new status history entry to the lead data
        leadData.statusHistory = [...(leadData.statusHistory || []), statusHistoryEntry]
        leadData.prevStatus = selectedLead.status
      }

      await updateLead({
        id: selectedLead.id,
        updates: leadData,
        statusChangeNote:
          data.status !== selectedLead.status
            ? `Status updated from ${selectedLead.status} to ${data.status}`
            : 'Status not updated',
      })
      onClearSelection()
    } else {
      // Check for duplicate leads before creating
      const duplicateReasons: string[] = []
      let existingLead: Lead | undefined

      // Check email
      if (data.email) {
        const emailMatch = leads?.find(lead => lead.email && lead.email === data.email)
        if (emailMatch) {
          duplicateReasons.push('email address')
          existingLead = emailMatch
        }
      }

      // Check phone (compare digits so formatted matches legacy unformatted stored values)
      if (data.phone && !existingLead) {
        const dataPhoneDigits = data.phone.replace(/\D/g, '')
        const phoneMatch = leads?.find(
          lead => lead.phone && lead.phone.replace(/\D/g, '') === dataPhoneDigits,
        )
        if (phoneMatch) {
          duplicateReasons.push('phone number')
          existingLead = phoneMatch
        }
      }

      // Check name and company together
      if (data.customerName && data.company && !existingLead) {
        const nameCompanyMatch = leads?.find(
          lead =>
            lead.customerName &&
            lead.customerName === data.customerName &&
            lead.company &&
            lead.company === data.company,
        )
        if (nameCompanyMatch) {
          duplicateReasons.push('customer name and company')
          existingLead = nameCompanyMatch
        }
      }

      if (existingLead && duplicateReasons.length > 0) {
        // Store form data and duplicate info for later use if user confirms
        setFormData(leadData)
        setDuplicateReason(duplicateReasons)
        setDuplicateLead(existingLead)
        // Open warning dialog
        setDuplicateWarningOpen(true)
        return
      }

      // No duplicate found, proceed with creation
      await createNewLead(leadData)
    }
  }

  // Function to create a new lead
  const createNewLead = async (leadData: Lead) => {
    // Set the creator ID for new leads
    leadData.createdBy = userInfo?.id || ''

    // Add initial status history entry for new leads with date as Date object
    const currentDate = new Date()
    const initialStatusEntry: StatusHistory = {
      id: `status_${Date.now()}`,
      previousStatus: null,
      newStatus: leadData.status as LeadStatus,
      changedBy: userInfo?.id || 'unknown',
      changedAt: currentDate, // Use Date object to match StatusHistory type
      notes: `Initial status set to ${leadData.status}`,
    }

    leadData.statusHistory = [...(leadData.statusHistory || []), initialStatusEntry]

    const created = await createLead(leadData)
    await onLeadCreated?.(created)

    const leadSizeKeys = getLeadVehicleAddedSizeKeys(leadData.truckRequest)
    const vehicleAddedType = notificationEnums.AdminNotificationType.ADMIN_INVENTORY_VEHICLE_ADDED
    const uncoveredSizes = notificationSubscriptions
      ? getUncoveredVehicleAddedSizeKeys(
          leadSizeKeys,
          notificationSubscriptions[vehicleAddedType],
        )
      : []

    if (uncoveredSizes.length > 0) {
      setPendingVehicleAddedSizes(uncoveredSizes)
      setVehicleAddedPromptOpen(true)
      return
    }

    onClearSelection()
  }

  const handleDismissVehicleAddedPrompt = () => {
    setVehicleAddedPromptOpen(false)
    setPendingVehicleAddedSizes([])
    onClearSelection()
  }

  const handleConfirmVehicleAddedPrompt = async () => {
    const vehicleAddedType = notificationEnums.AdminNotificationType.ADMIN_INVENTORY_VEHICLE_ADDED

    if (!notificationSubscriptions) {
      showError(
        'Lead was created, but notification preferences could not be loaded. Enable Vehicle Added sizes in the Notification Center.',
      )
      handleDismissVehicleAddedPrompt()
      return
    }

    const currentPrefs = notificationSubscriptions[vehicleAddedType] ?? {
      email: false,
      sms: false,
      inApp: false,
    }
    const channelOn = !!(currentPrefs.inApp || currentPrefs.email || currentPrefs.sms)
    const nextSizes = [
      ...new Set([...(currentPrefs.sizes ?? []), ...pendingVehicleAddedSizes]),
    ]

    const updatedPreferences: NotificationPreferences = {
      ...notificationSubscriptions,
      [vehicleAddedType]: {
        ...currentPrefs,
        inApp: channelOn ? currentPrefs.inApp : true,
        sizes: nextSizes,
      },
    }

    setEnablingVehicleAddedSizes(true)
    try {
      await updateNotificationPreferences.mutateAsync(updatedPreferences)
    } catch {
      showError('Lead was created, but Vehicle Added notifications could not be updated.')
    } finally {
      setEnablingVehicleAddedSizes(false)
      handleDismissVehicleAddedPrompt()
    }
  }

  // Function to handle confirmation of duplicate lead creation
  const handleConfirmDuplicate = async () => {
    if (formData) {
      await createNewLead(formData)
    }
    setDuplicateWarningOpen(false)
    setFormData(null)
    setDuplicateReason([])
    setDuplicateLead(null)
  }

  // Function to handle cancellation of duplicate lead creation
  const handleCancelDuplicate = () => {
    setDuplicateWarningOpen(false)
    setFormData(null)
    setDuplicateReason([])
    setDuplicateLead(null)
  }

  // Format vehicle display for selection
  const formatVehicleDisplay = (vehicle: Vehicle) => {
    return `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''} - ${vehicle.stock_number || ''}`
  }

  // Reset form when selectedLead changes (open edit vs create). Do NOT depend on users
  // so that when we create a new client and users refetches, we don't wipe the prefilled fields.
  useEffect(() => {
    setMustRelinkClient(false)
    setClientLinkRequiredError(false)
    appliedLinkedClientForLeadIdRef.current = null
    if (selectedLead) {
      const leadData = { ...selectedLead }
      if (leadData.truckRequest && !leadData.truckRequest.types) {
        leadData.truckRequest = { types: [], notes: '' }
      }
      reset({
        ...leadData,
        phone: leadData.phone ? formatDigitsAsUsPhone(leadData.phone) : '',
        client_id: leadData.client_id ?? null,
        companyId: leadData.companyId ?? null,
        businessAddressId: leadData.businessAddressId ?? null,
        businessContact: {
          name: leadData.businessContact?.name ?? '',
          email: leadData.businessContact?.email ?? '',
          phone: leadData.businessContact?.phone
            ? formatDigitsAsUsPhone(leadData.businessContact.phone)
            : '',
          role: leadData.businessContact?.role ?? undefined,
        },
      })
    } else {
      setSelectedUser(null)
      reset({
        status: 'new' as LeadStatus,
        prevStatus: null,
        financingStatus: '',
        timeFrame: '',
        source: '',
        priority: 'medium',
        truckIds: [],
        applicationIds: [],
        followUpNotes: [],
        statusHistory: [],
        tags: [],
        truckRequest: { types: [], notes: '' },
        client_id: null,
        contactId: null,
        companyId: null,
        businessAddressId: null,
        truckDropoffLocationIds: {},
        businessContact: { name: '', email: '', phone: '', role: undefined },
      })
    }
  }, [selectedLead, reset])

  // Seed CRM contact id when creating from a Contact / Company surface.
  useEffect(() => {
    if (!isCreating || selectedLead) return
    const id = initialContactId?.trim()
    if (!id) return
    setValue('contactId', id, { shouldDirty: false })
  }, [initialContactId, isCreating, selectedLead, setValue])

  // When editing a lead, resolve selectedUser once from client_id (preferred) or email (legacy)
  useEffect(() => {
    if (!selectedLead || !users?.length) return
    if (appliedLinkedClientForLeadIdRef.current === selectedLead.id) return

    if (selectedLead.client_id) {
      const byId = users.find((user: PrivateUserInfoType) => user.id === selectedLead.client_id)
      if (byId) {
        appliedLinkedClientForLeadIdRef.current = selectedLead.id
        setSelectedUser(byId)
        return
      }
    }

    if (selectedLead.email) {
      const matchingUser = users.find(
        (user: PrivateUserInfoType) => user.email === selectedLead.email,
      )
      if (matchingUser) {
        appliedLinkedClientForLeadIdRef.current = selectedLead.id
        setSelectedUser(matchingUser)
        return
      }
    }

    // No match yet; mark as applied so a later users refresh with no match doesn't keep retrying
    // in a way that fights a manual clear. Retry only when client_id/email become matchable.
    if (!selectedLead.client_id && !selectedLead.email) {
      appliedLinkedClientForLeadIdRef.current = selectedLead.id
    }
  }, [selectedLead?.id, selectedLead?.client_id, selectedLead?.email, users])

  // When opening Create Lead with a pre-selected client (e.g. from Create User "Create lead"), apply once
  useEffect(() => {
    if (!initialClient) {
      appliedInitialClientIdRef.current = null
      return
    }
    if (!isCreating || selectedLead) return
    if (appliedInitialClientIdRef.current === initialClient.id) return

    const matchedUser =
      users?.find((u) => u.id === initialClient.id) ??
      (initialClient.email
        ? users?.find(
            (u) =>
              u.email &&
              u.email.toLowerCase() === initialClient.email.trim().toLowerCase(),
          )
        : undefined)

    const initialCustomerName =
      initialClient.displayName?.trim() ||
      `${initialClient.first_name ?? ''} ${initialClient.last_name ?? ''}`.trim()
    const initialFieldFallbacks: ClientFieldFallbacks = {
      customerName: initialCustomerName,
      email: initialClient.email,
      phone: initialClient.phone,
      company: initialClient.company_name,
    }

    if (matchedUser) {
      appliedInitialClientIdRef.current = initialClient.id
      // User wins for present fields; contact / Create User payload fills gaps (esp. phone).
      handleUserSelect(matchedUser, initialFieldFallbacks)
      return
    }

    // Prefill from the contact / Create User payload while users may still be loading.
    setValue('client_id', initialClient.id, { shouldDirty: true })
    setValue('customerName', initialCustomerName, { shouldDirty: true, shouldValidate: true })
    setValue('email', initialClient.email || '', { shouldDirty: true, shouldValidate: true })
    setValue('phone', initialClient.phone ? formatDigitsAsUsPhone(initialClient.phone) : '', {
      shouldDirty: true,
      shouldValidate: true,
    })
    setValue('company', initialClient.company_name || '', { shouldDirty: true, shouldValidate: true })
    setUserApplications([])

    // Only lock after we have a users list and confirmed no portal match (retry when users arrives).
    if (users?.length) {
      appliedInitialClientIdRef.current = initialClient.id
    }
  }, [initialClient, isCreating, selectedLead, setValue, users])

  // CRM contact without a portal user: prefill basics only; client search stays visible
  useEffect(() => {
    if (!contactPrefill) {
      appliedContactPrefillKeyRef.current = null
      return
    }
    if (!isCreating || selectedLead) return
    const key = [
      contactPrefill.customerName,
      contactPrefill.email ?? '',
      contactPrefill.phone ?? '',
      contactPrefill.company ?? '',
    ].join('|')
    if (appliedContactPrefillKeyRef.current === key) return
    appliedContactPrefillKeyRef.current = key

    setValue('customerName', contactPrefill.customerName, {
      shouldDirty: true,
      shouldValidate: true,
    })
    if (contactPrefill.email) {
      setValue('email', contactPrefill.email, { shouldDirty: true, shouldValidate: true })
    }
    if (contactPrefill.phone) {
      setValue('phone', formatDigitsAsUsPhone(contactPrefill.phone), {
        shouldDirty: true,
        shouldValidate: true,
      })
    }
    if (contactPrefill.company) {
      setValue('company', contactPrefill.company, { shouldDirty: true, shouldValidate: true })
    }
  }, [contactPrefill, isCreating, selectedLead, setValue])

  // When creating a lead with a selected ContactCompany, prefill company name,
  // company id, and business address from the company record. Member contacts
  // are surfaced via the Business Contact picker further down.
  useEffect(() => {
    if (!selectedCompany) {
      appliedSelectedCompanyIdRef.current = null
      return
    }
    if (!isCreating || selectedLead) return
    if (appliedSelectedCompanyIdRef.current === selectedCompany.id) return
    appliedSelectedCompanyIdRef.current = selectedCompany.id

    setValue('company', selectedCompany.name || '', {
      shouldDirty: true,
      shouldValidate: true,
    })
    setValue('companyId', selectedCompany.id, {
      shouldDirty: true,
      shouldValidate: true,
    })
    if (selectedCompany.businessAddressId) {
      setValue('businessAddressId', selectedCompany.businessAddressId, {
        shouldDirty: true,
        shouldValidate: true,
      })
      const business = resolvePickupLocationById(
        savedPickupLocations,
        selectedCompany.businessAddressId,
      )
      if (business?.state?.trim()) {
        setValue('location', business.state.trim(), {
          shouldDirty: true,
          shouldValidate: false,
        })
      }
    }
  }, [selectedCompany, isCreating, selectedLead, setValue, savedPickupLocations])

  /** Apply a company member to the lead's business-contact fields (including role). */
  const applyCompanyMemberToBusinessContact = (member: CompanyMemberForLead | null) => {
    if (!member) {
      setValue('businessContact.name', '', { shouldDirty: true, shouldValidate: true })
      setValue('businessContact.email', '', { shouldDirty: true, shouldValidate: true })
      setValue('businessContact.phone', '', { shouldDirty: true, shouldValidate: true })
      setValue('businessContact.role', undefined, { shouldDirty: true, shouldValidate: true })
      return
    }
    const { contact, role } = member
    setValue('businessContact.name', contact.name || '', {
      shouldDirty: true,
      shouldValidate: true,
    })
    setValue('businessContact.email', contact.email || '', {
      shouldDirty: true,
      shouldValidate: true,
    })
    setValue(
      'businessContact.phone',
      contact.phone ? formatDigitsAsUsPhone(contact.phone) : '',
      { shouldDirty: true, shouldValidate: true },
    )
    setValue('businessContact.role', role, { shouldDirty: true, shouldValidate: true })
  }

  // After company addresses are prefilled, select the default member contact + role.
  useEffect(() => {
    if (!defaultBusinessContactId) {
      appliedDefaultBusinessContactIdRef.current = null
      return
    }
    if (!isCreating || selectedLead || !selectedCompany || !companyMembers?.length) return
    if (appliedDefaultBusinessContactIdRef.current === defaultBusinessContactId) return
    appliedDefaultBusinessContactIdRef.current = defaultBusinessContactId
    const member = companyMembers.find((m) => m.contact.id === defaultBusinessContactId)
    if (member) applyCompanyMemberToBusinessContact(member)
  }, [
    defaultBusinessContactId,
    isCreating,
    selectedLead,
    selectedCompany,
    companyMembers,
    setValue,
  ])

  /** Whether the Business Contact section should be a picker (driven from a company). */
  const useCompanyBusinessContactPicker = Boolean(
    isCreating && selectedCompany && companyMembers && companyMembers.length > 0,
  )

  const leadLocationMemberContacts = useMemo(() => {
    if (companyMembers?.length) {
      return companyMembers.map((member) => member.contact)
    }
    const name = companyField?.trim()
    if (!name) return []
    const match = contactCompanies.find(
      (company) => company.name.trim().toLowerCase() === name.toLowerCase(),
    )
    if (!match) return []
    return sortCompanyMemberContacts(
      match.members.map((member) => ({ contactId: member.contactId, role: member.role })),
      crmContacts,
    )
  }, [companyMembers, companyField, contactCompanies, crmContacts])

  return (
    <Box>
      <Box
        sx={{
          mb: 2.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {isSmallScreen ? (
          <Stack direction='row' alignItems='center' sx={{ pb: 1 }}>
            <Stack direction='row' spacing={1} alignItems='center' sx={{ flexGrow: 1 }}>
              {FORM_TABS[tabValue]?.icon}
              <Typography variant='subtitle2' fontWeight={700}>
                {FORM_TABS[tabValue]?.label}
              </Typography>
            </Stack>
            <IconButton edge='end' size='small' aria-label='menu' onClick={handleMobileMenuOpen}>
              <MenuIcon />
            </IconButton>
            <Menu anchorEl={mobileMenuAnchor} open={Boolean(mobileMenuAnchor)} onClose={handleMobileMenuClose}>
              {FORM_TABS.map((tab, idx) => (
                <MenuItem
                  key={tab.label}
                  onClick={() => handleMobileMenuSelect(idx)}
                  selected={tabValue === idx}
                >
                  <Stack direction='row' spacing={1} alignItems='center'>
                    {tab.icon}
                    <Typography variant='body2'>{tab.label}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Menu>
          </Stack>
        ) : (
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant={isMobile ? 'fullWidth' : 'standard'}
            sx={{
              minHeight: 44,
              '& .MuiTab-root': {
                minHeight: 44,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
              },
            }}
          >
            {FORM_TABS.map(tab => (
              <Tab
                key={tab.label}
                icon={tab.icon}
                iconPosition='start'
                label={tab.label}
              />
            ))}
          </Tabs>
        )}
      </Box>

      <Dialog
        open={duplicateWarningOpen}
        onClose={handleCancelDuplicate}
        maxWidth='sm'
        fullWidth
        aria-labelledby='duplicate-lead-dialog-title'
        aria-describedby='duplicate-lead-dialog-description'
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          id='duplicate-lead-dialog-title'
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.15)} 0%, ${alpha(theme.palette.warning.main, 0.04)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
          }}
        >
          <Avatar
            variant='rounded'
            sx={{
              bgcolor: alpha(theme.palette.warning.main, 0.18),
              color: theme.palette.warning.main,
              width: 40,
              height: 40,
            }}
          >
            <WarningAmberIcon />
          </Avatar>
          <Box>
            <Typography variant='caption' sx={{ color: theme.palette.warning.dark, fontWeight: 700, letterSpacing: 1 }}>
              POTENTIAL DUPLICATE
            </Typography>
            <Typography variant='h6' fontWeight={700} sx={{ lineHeight: 1.2 }}>
              Duplicate Lead Warning
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText id='duplicate-lead-dialog-description' sx={{ mb: 2 }}>
            A lead with the same <strong>{duplicateReason.join(' and ')}</strong> already exists.
          </DialogContentText>

          {duplicateLead && (
            <Paper
              variant='outlined'
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.warning.main, 0.04),
                borderColor: alpha(theme.palette.warning.main, 0.3),
              }}
            >
              <Typography
                variant='caption'
                sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700, color: 'text.secondary' }}
              >
                Existing lead
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                <Typography variant='body2'>
                  <strong>Name:</strong> {duplicateLead.customerName}
                </Typography>
                {duplicateLead.company && (
                  <Typography variant='body2'>
                    <strong>Company:</strong> {duplicateLead.company}
                  </Typography>
                )}
                {duplicateLead.email && (
                  <Typography variant='body2'>
                    <strong>Email:</strong> {duplicateLead.email}
                  </Typography>
                )}
                {duplicateLead.phone && (
                  <Typography variant='body2'>
                    <strong>Phone:</strong> {duplicateLead.phone}
                  </Typography>
                )}
                <Typography variant='body2'>
                  <strong>Status:</strong> {duplicateLead.status}
                </Typography>
              </Stack>
            </Paper>
          )}

          <Typography variant='body2' sx={{ mt: 2.5 }} color='text.secondary'>
            Do you want to create a new lead anyway?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelDuplicate}>Cancel</Button>
          <Button onClick={handleConfirmDuplicate} variant='contained' color='warning' autoFocus>
            Create anyway
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={vehicleAddedPromptOpen}
        onClose={enablingVehicleAddedSizes ? undefined : handleDismissVehicleAddedPrompt}
        maxWidth='sm'
        fullWidth
        aria-labelledby='vehicle-added-size-prompt-title'
        aria-describedby='vehicle-added-size-prompt-description'
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          id='vehicle-added-size-prompt-title'
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.15)} 0%, ${alpha(theme.palette.info.main, 0.04)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
          }}
        >
          <Avatar
            variant='rounded'
            sx={{
              bgcolor: alpha(theme.palette.info.main, 0.18),
              color: theme.palette.info.main,
              width: 40,
              height: 40,
            }}
          >
            <NotificationsActiveIcon />
          </Avatar>
          <Box>
            <Typography
              variant='caption'
              sx={{ color: theme.palette.info.dark, fontWeight: 700, letterSpacing: 1 }}
            >
              VEHICLE ADDED NOTIFICATIONS
            </Typography>
            <Typography variant='h6' fontWeight={700} sx={{ lineHeight: 1.2 }}>
              Turn on for this size?
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText id='vehicle-added-size-prompt-description' sx={{ mb: 2 }}>
            You do not receive &quot;Vehicle Added&quot; notifications for the following{' '}
            {pendingVehicleAddedSizes.length === 1 ? 'size' : 'sizes'} associated with this lead:
          </DialogContentText>
          <Stack direction='row' flexWrap='wrap' gap={1} sx={{ mb: 2 }}>
            {pendingVehicleAddedSizes.map(key => (
              <Chip key={key} label={formatVehicleAddedSizeKeyLabel(key)} color='info' variant='outlined' />
            ))}
          </Stack>
          <DialogContentText>
            Would you like to turn on notifications for{' '}
            {pendingVehicleAddedSizes.length === 1 ? 'this size' : 'these sizes'}?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDismissVehicleAddedPrompt} disabled={enablingVehicleAddedSizes}>
            No thanks
          </Button>
          <Button
            onClick={handleConfirmVehicleAddedPrompt}
            variant='contained'
            color='primary'
            autoFocus
            disabled={enablingVehicleAddedSizes}
            startIcon={
              enablingVehicleAddedSizes ? <CircularProgress size={16} color='inherit' /> : undefined
            }
          >
            Yes, turn on
          </Button>
        </DialogActions>
      </Dialog>

      <form onSubmit={handleSubmit(onSubmit)}>
        <TabPanel value={tabValue} index={0}>
          <Stack spacing={2.5}>
            {clientLinkRequiredError && (
              <Alert severity='error'>
                {mustRelinkClient
                  ? 'Select a new client to link before saving this lead.'
                  : 'Link an existing client account (or create one) before saving this lead.'}
              </Alert>
            )}
            {contactPrefill && isCreating && !selectedUser && (
              <Alert severity='info'>
                This contact does not have a portal client account yet. Search below to link an
                existing client or create a new account — their name and contact details are
                prefilled from the CRM record.
              </Alert>
            )}
            {!hideClientSearch && !selectedUser && (
              <Paper
                variant='outlined'
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(accent, 0.04),
                  borderColor: alpha(accent, 0.25),
                }}
              >
                <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
                  <PersonIcon sx={{ fontSize: 18, color: accent }} />
                  <Typography
                    variant='caption'
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 700, color: accent }}
                  >
                    Link a client
                  </Typography>
                </Stack>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                  Search for an existing client to auto-fill their info, or create a new one.
                </Typography>
                <UserSelector
                  onUserSelect={handleUserSelect}
                  title='Select Client'
                  placeholder='Search clients...'
                  showCreateButton={true}
                />
              </Paper>
            )}

            {selectedUser && !hideClientSearch && (
              <Paper
                variant='outlined'
                sx={{
                  p: 2,
                  borderRadius: 2,
                  borderColor: alpha(accent, 0.3),
                  bgcolor: alpha(accent, 0.04),
                }}
              >
                <Stack direction='row' spacing={1.5} alignItems='center'>
                  <Avatar
                    sx={{
                      bgcolor: alpha(accent, 0.18),
                      color: accent,
                      fontWeight: 700,
                      width: 40,
                      height: 40,
                    }}
                  >
                    {(selectedUser.first_name?.[0] || '') + (selectedUser.last_name?.[0] || '')}
                  </Avatar>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <Typography variant='subtitle2' fontWeight={700} noWrap>
                        {selectedUser.first_name} {selectedUser.last_name}
                      </Typography>
                      <Chip size='small' label='Linked client' color='primary' sx={{ height: 18, fontSize: '0.65rem' }} />
                    </Stack>
                    <Typography variant='caption' color='text.secondary' noWrap sx={{ display: 'block' }}>
                      {selectedUser.email}
                      {selectedUser.company_name ? ` · ${selectedUser.company_name}` : ''}
                    </Typography>
                    {userApplications.length > 0 && (
                      <Typography variant='caption' color='primary' fontWeight={600}>
                        {userApplications.length} application{userApplications.length !== 1 ? 's' : ''} found
                      </Typography>
                    )}
                  </Box>
                  <IconButton size='small' onClick={handleClearUser} aria-label='Unlink client'>
                    <ClearIcon fontSize='small' />
                  </IconButton>
                </Stack>
              </Paper>
            )}

            {selectedUser && (
              <Paper variant='outlined' sx={{ p: 2, borderRadius: 2 }}>
                <Typography
                  variant='caption'
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.7,
                    fontWeight: 700,
                    color: 'text.secondary',
                    display: 'block',
                    mb: 1.5,
                  }}
                >
                  Driver&apos;s License
                </Typography>
                <ClientDriversLicenseSection
                  clientUser={selectedClientForLicense}
                  canEdit={hasLeadEditPermission}
                />
              </Paper>
            )}

            <Box>
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.5 }}>
                <DescriptionIcon sx={{ fontSize: 18, color: 'text.primary' }} />
                <Typography
                  variant='caption'
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.7,
                    fontWeight: 700,
                    color: 'text.primary',
                  }}
                >
                  Client information
                </Typography>
                <Box sx={{ flexGrow: 1, height: 1, bgcolor: 'divider' }} />
              </Stack>
              <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label='Customer Name'
                      {...register('customerName', { required: 'Customer name is required' })}
                      error={!!errors.customerName}
                      helperText={errors.customerName?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label='Company'
                      {...register('company', { required: 'Company is required' })}
                      error={!!errors.company}
                      helperText={errors.company?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label='Email'
                      type='email'
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address',
                        },
                      })}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name='phone'
                      control={control}
                      rules={{ required: 'Phone is required' }}
                      render={({ field }) => (
                        <TextField
                          fullWidth
                          label='Phone'
                          placeholder='(123) 456-7890'
                          value={field.value || ''}
                          onChange={e => field.onChange(formatDigitsAsUsPhone(e.target.value))}
                          onBlur={field.onBlur}
                          name={field.name}
                          inputRef={field.ref}
                          error={!!errors.phone}
                          helperText={errors.phone?.message || 'Format: (123) 456-7890'}
                          InputLabelProps={{ shrink: !!field.value }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 0.5, mt: 1 }}>
                      <ContactsIcon sx={{ fontSize: 18, color: 'text.primary' }} />
                      <Typography
                        variant='caption'
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: 0.7,
                          fontWeight: 700,
                          color: 'text.primary',
                        }}
                      >
                        Business contact
                      </Typography>
                      <Box sx={{ flexGrow: 1, height: 1, bgcolor: 'divider' }} />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {useCompanyBusinessContactPicker ? (
                      <Autocomplete<CompanyMemberForLead>
                        options={companyMembers ?? []}
                        value={
                          (companyMembers ?? []).find(
                            (m) => m.contact.name === watch('businessContact.name'),
                          ) ?? null
                        }
                        onChange={(_, newValue) => applyCompanyMemberToBusinessContact(newValue)}
                        getOptionLabel={(option) => option.contact.name || ''}
                        isOptionEqualToValue={(opt, val) => opt.contact.id === val.contact.id}
                        renderOption={(props, option) => (
                          <Box component='li' {...props} key={option.contact.id}>
                            <Box>
                              <Typography variant='body2' fontWeight='medium'>
                                {option.contact.name || 'Unnamed contact'}
                              </Typography>
                              {option.contact.email || option.contact.phone ? (
                                <Typography variant='caption' color='text.secondary'>
                                  {[option.contact.email, option.contact.phone]
                                    .filter(Boolean)
                                    .join(' • ')}
                                </Typography>
                              ) : null}
                            </Box>
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            label='Name'
                            error={!!errors.businessContact?.name}
                            helperText={
                              errors.businessContact?.name?.message ||
                              `Pick from ${companyMembers?.length ?? 0} ${
                                (companyMembers?.length ?? 0) === 1 ? 'contact' : 'contacts'
                              } at this company`
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        label='Name'
                        {...register('businessContact.name')}
                        error={!!errors.businessContact?.name}
                        helperText={errors.businessContact?.name?.message}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id='business-contact-role-label'>Role</InputLabel>
                      <Controller
                        name='businessContact.role'
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId='business-contact-role-label'
                            label='Role'
                            value={field.value ?? ''}
                            onChange={e => {
                              const value = e.target.value as LeadBusinessContactRole | ''
                              field.onChange(value || undefined)
                            }}
                          >
                            <MenuItem value=''>
                              <em>None</em>
                            </MenuItem>
                            {BUSINESS_CONTACT_ROLES.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label='Email'
                      type='email'
                      {...register('businessContact.email', {
                        validate: v =>
                          !v?.trim() ||
                          /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(v.trim()) ||
                          'Invalid email address',
                      })}
                      error={!!errors.businessContact?.email}
                      helperText={errors.businessContact?.email?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label='Phone'
                      placeholder='(123) 456-7890'
                      value={watch('businessContact.phone') || ''}
                      onChange={e =>
                        setValue('businessContact.phone', formatDigitsAsUsPhone(e.target.value), {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                      error={!!errors.businessContact?.phone}
                      helperText={errors.businessContact?.phone?.message?.toString()}
                      InputLabelProps={{ shrink: !!watch('businessContact.phone') }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.5, mt: 0.5 }}>
                      <ApartmentIcon sx={{ fontSize: 18, color: accent }} />
                      <Typography
                        variant='caption'
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: 0.7,
                          fontWeight: 700,
                          color: accent,
                        }}
                      >
                        Business address
                      </Typography>
                      <Box sx={{ flexGrow: 1, height: 1, bgcolor: 'divider' }} />
                    </Stack>
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name='businessAddressId'
                      control={control}
                      render={({ field }) => (
                        <PickupLocationSelector
                          label='Business address (optional)'
                          addressKind='business'
                          value={field.value}
                          onChange={field.onChange}
                          companyName={watch('company')}
                          memberContacts={leadLocationMemberContacts}
                          allowPartialAddress
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.5, mt: 0.5 }}>
                      <DescriptionIcon sx={{ fontSize: 18, color: 'text.primary' }} />
                      <Typography
                        variant='caption'
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: 0.7,
                          fontWeight: 700,
                          color: 'text.primary',
                        }}
                      >
                        Lead information
                      </Typography>
                      <Box sx={{ flexGrow: 1, height: 1, bgcolor: 'divider' }} />
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id='assigned-rep-label'>Assigned Representative</InputLabel>
                      <Controller
                        name='assignedRepId'
                        control={control}
                        rules={{ required: 'Representative is required' }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            value={
                              field.value !== null && field.value !== undefined
                                ? String(field.value)
                                : ''
                            }
                            onChange={(e) => {
                              const v = e.target.value
                              const n = Number(v)
                              field.onChange(
                                Number.isFinite(n) && String(n) === v ? n : v,
                              )
                            }}
                            labelId='assigned-rep-label'
                            label='Assigned Representative'
                            error={!!errors.assignedRepId}
                            disabled={isLoadingAssignableReps}
                          >
                            {assignableReps.map((user) => (
                              <MenuItem key={String(user.id)} value={String(user.id)}>
                                {getUserDisplayName(user)}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {errors.assignedRepId && <FormHelperText error>{errors.assignedRepId.message}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id='status-label'>Lead Status</InputLabel>
                      <Controller
                        name='status'
                        control={control}
                        rules={{ required: 'Status is required' }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            value={field.value ?? ''}
                            labelId='status-label'
                            label='Lead Status'
                            error={!!errors.status}
                          >
                            {leadStatuses.map(status => (
                              <MenuItem key={status} value={status}>
                                {status === 'new'
                                  ? 'New'
                                  : status === 'inProgress'
                                    ? 'In Progress'
                                    : status === 'waitingFinance'
                                      ? 'Waiting Finance'
                                      : status === 'closed'
                                        ? 'Closed'
                                        : 'Lost'}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {errors.status && <FormHelperText error>{errors.status.message}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id='financing-status-label'>Financing Status</InputLabel>
                      <Controller
                        name='financingStatus'
                        control={control}
                        rules={{ required: 'Financing status is required' }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            value={field.value ?? ''}
                            labelId='financing-status-label'
                            label='Financing Status'
                            error={!!errors.financingStatus}
                          >
                            {financingStatuses.map(status => (
                              <MenuItem key={status} value={status}>
                                {status}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {errors.financingStatus && (
                        <FormHelperText error>{errors.financingStatus.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id='time-frame-label'>Time Frame</InputLabel>
                      <Controller
                        name='timeFrame'
                        control={control}
                        rules={{ required: 'Time frame is required' }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            value={field.value ?? ''}
                            labelId='time-frame-label'
                            label='Time Frame'
                            error={!!errors.timeFrame}
                          >
                            <MenuItem value='7 days'>7 days</MenuItem>
                            <MenuItem value='1 month'>1 month</MenuItem>
                            <MenuItem value='2 months'>2 months</MenuItem>
                            <MenuItem value='6 months'>6 months</MenuItem>
                            <MenuItem value='1 year'>1 year</MenuItem>
                          </Select>
                        )}
                      />
                      {errors.timeFrame && <FormHelperText error>{errors.timeFrame.message}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name='priority'
                      control={control}
                      defaultValue='medium'
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel id='priority-label'>Priority</InputLabel>
                          <Select {...field} value={field.value ?? ''} labelId='priority-label' label='Priority'>
                            <MenuItem value='low'>Low</MenuItem>
                            <MenuItem value='medium'>Medium</MenuItem>
                            <MenuItem value='high'>High</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id='source-label'>Lead Source</InputLabel>
                      <Controller
                        name='source'
                        control={control}
                        rules={{ required: 'Lead source is required' }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            value={field.value ?? ''}
                            labelId='source-label'
                            label='Lead Source'
                            error={!!errors.source}
                          >
                            <MenuItem value='Referred'>Referred</MenuItem>
                            <MenuItem value='Repeat Customer'>Repeat Customer</MenuItem>
                            <MenuItem value='Internet'>Sourced from Internet</MenuItem>
                            <MenuItem value='Other'>Other</MenuItem>
                          </Select>
                        )}
                      />
                      {errors.source && <FormHelperText error>{errors.source.message}</FormHelperText>}
                    </FormControl>
                  </Grid>

                  {/* Add conditional text field for "other" source */}
                  {watch('source') === 'Other' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Specify Other Source'
                        value={otherSource}
                        onChange={e => {
                          setOtherSource(e.target.value)
                        }}
                        error={!otherSource}
                        helperText={!otherSource ? 'Please specify the source' : ''}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Controller
                      name='tags'
                      control={control}
                      defaultValue={[]}
                      render={({ field }) => (
                        <Autocomplete
                          {...field}
                          multiple
                          freeSolo
                          options={[]}
                          renderTags={(value: string[], getTagProps) =>
                            value.map((option: string, index: number) => (
                              <Chip variant='outlined' label={option} {...getTagProps({ index })} />
                            ))
                          }
                          renderInput={params => (
                            <TextField {...params} variant='outlined' label='Tags' placeholder='Add tags' />
                          )}
                          onChange={(_, data) => field.onChange(data)}
                        />
                      )}
                    />
                  </Grid>
              </Grid>
            </Box>
          </Stack>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <TruckRequestForm
            register={register}
            errors={errors.truckRequest as FieldErrors<TruckRequest>}
            control={control}
            setValue={setValue}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Stack spacing={3}>
            <Box>
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.5 }}>
                <LocalShippingIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography
                  variant='caption'
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.7,
                    fontWeight: 700,
                    color: 'text.secondary',
                  }}
                >
                  Link to inventory
                </Typography>
                <Box sx={{ flexGrow: 1, height: 1, bgcolor: 'divider' }} />
              </Stack>
            </Box>
            <Controller
              name='truckIds'
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  multiple
                  options={activeVehicles || []}
                  getOptionLabel={option => {
                    if (typeof option === 'string') {
                      const vehicle = activeVehicles?.find(v => v.id === option)
                      return vehicle ? formatVehicleDisplay(vehicle) : option
                    }
                    return formatVehicleDisplay(option)
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant='outlined'
                      label='Select Active Vehicles'
                      placeholder='Search vehicles'
                    />
                  )}
                  renderOption={(props, option) => {
                    // Ensure option is a Vehicle object, not a string
                    if (typeof option === 'string') {
                      const vehicleObj = activeVehicles?.find(v => v.id === option)
                      if (!vehicleObj) return null

                      return (
                        <li {...props}>
                          <Box>
                            <Typography variant='body1'>{formatVehicleDisplay(vehicleObj)}</Typography>
                            <Typography variant='caption' color='text.secondary'>
                              Status: {vehicleObj.status || 'N/A'} | Type: {vehicleObj.type || 'N/A'} | Condition:{' '}
                              {vehicleObj.condition || 'N/A'}
                            </Typography>
                          </Box>
                        </li>
                      )
                    }

                    return (
                      <li {...props}>
                        <Box>
                          <Typography variant='body1'>{formatVehicleDisplay(option)}</Typography>
                          <Typography variant='caption' color='text.secondary'>
                            Status: {option.status || 'N/A'} | Type: {option.type || 'N/A'} | Condition:{' '}
                            {option.condition || 'N/A'}
                          </Typography>
                        </Box>
                      </li>
                    )
                  }}
                  onChange={(_, data) => {
                    const ids = data.map(item => {
                      return typeof item === 'string' ? item : item.id
                    })
                    field.onChange(ids)
                  }}
                  isOptionEqualToValue={(option, value) => {
                    const optionId = typeof option === 'string' ? option : option.id
                    const valueId = typeof value === 'string' ? value : value.id
                    return optionId === valueId
                  }}
                  value={field.value.map((id: string) => {
                    return activeVehicles?.find(vehicle => vehicle.id === id) || id
                  })}
                />
              )}
            />

            <Box>
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.5, mt: 1 }}>
                <LinkIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography
                  variant='caption'
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.7,
                    fontWeight: 700,
                    color: 'text.secondary',
                  }}
                >
                  Link to applications
                </Typography>
                <Box sx={{ flexGrow: 1, height: 1, bgcolor: 'divider' }} />
              </Stack>
              {userApplications.length > 0 && (
                <Paper
                  variant='outlined'
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(accent, 0.04),
                    borderColor: alpha(accent, 0.25),
                  }}
                >
                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 0.75 }}>
                    Applications found for selected client:
                  </Typography>
                  <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
                    {userApplications.map(appId => (
                      <Chip
                        key={appId}
                        label={`App ID: ${appId.substring(0, 8)}...`}
                        color='primary'
                        variant='outlined'
                        size='small'
                      />
                    ))}
                  </Stack>
                </Paper>
              )}
            </Box>
            <Controller
              name='applicationIds'
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label='Application IDs (comma separated)'
                  placeholder='Enter application IDs'
                  onChange={e => {
                    const ids = e.target.value
                      .split(',')
                      .map(id => id.trim())
                      .filter(id => id)
                    field.onChange(ids)
                  }}
                  value={field.value.join(', ')}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Stack>
        </TabPanel>

        <Box
          sx={{
            mt: 3,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexDirection: { xs: 'column-reverse', sm: 'row' },
          }}
        >
          {!hideCancel ? (
            <Button onClick={() => onClearSelection()} color='inherit' fullWidth={isSmallScreen}>
              Cancel
            </Button>
          ) : (
            <Box />
          )}
          <Button
            type='submit'
            variant='contained'
            size='large'
            fullWidth={isSmallScreen}
            disabled={
              ((isCreating && requireLinkedClient) || mustRelinkClient) && !selectedUser
            }
            sx={{ fontWeight: 700, px: 3 }}
          >
            {selectedLead ? 'Update lead' : 'Create lead'}
          </Button>
        </Box>
      </form>

    </Box>
  )
}
